import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, DollarSign, User, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

const MaintenanceLog = ({ records, onAdd, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    details: '',
    cost: '',
    technician: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({
      ...formData,
      cost: formData.cost ? parseFloat(formData.cost) : null
    });
    setFormData({
      date: new Date().toISOString().split('T')[0],
      details: '',
      cost: '',
      technician: ''
    });
    setShowForm(false);
  };

  const sortedRecords = [...records].sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-medium text-foreground">Maintenance Log</h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center space-x-2"
        >
          {showForm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span>{showForm ? 'Cancel' : 'Add Entry'}</span>
        </Button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden mb-6"
          >
            <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <Input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Cost</label>
                  <Input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Technician/Company</label>
                <Input
                  name="technician"
                  value={formData.technician}
                  onChange={handleChange}
                  placeholder="e.g., ABC Plumbing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Details</label>
                <textarea
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  className="flex w-full rounded-lg border border-input bg-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px]"
                  placeholder="Describe the maintenance performed..."
                  required
                />
              </div>
              <Button type="submit" className="w-full sm:w-auto">
                Add Record
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {sortedRecords.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-body">No maintenance records yet. Add one to get started!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedRecords.map((record, index) => (
            <motion.div
              key={record.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/5 rounded-lg border border-white/10 p-4"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="font-medium text-foreground">
                    {new Date(record.date).toLocaleDateString()}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(record.id)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {record.details && (
                <p className="text-sm text-body mb-2">{record.details}</p>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-body">
                {record.cost && (
                  <div className="flex items-center space-x-1">
                    <DollarSign className="h-3 w-3" />
                    <span>${parseFloat(record.cost).toFixed(2)}</span>
                  </div>
                )}
                {record.technician && (
                  <div className="flex items-center space-x-1">
                    <User className="h-3 w-3" />
                    <span>{record.technician}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaintenanceLog;

